import React from 'react';
import style from './style.module.scss';
import { Tooltip } from 'antd';
import { TrendArrow } from '~components/trend-arrow';
import { formatDurationSeconds } from '~containers/analytics/kpis/format-duration';
import { formatRate } from '~containers/analytics/kpis/format-rate';
import { useTweenedValue } from './use-tweened-value';
import { formatTweenedNumber } from './tween-format';
import type { KpiTrend } from '~containers/analytics/kpis/types';

export type { KpiTrend };

export interface KpiTileData {
    title?: string;
    value: number | string;
    previousValue?: number;
    description?: string;
    isCurrency?: boolean;
    /** `value` is the raw 0..1 rate; the tile tweens it as a number and
     * formats the result like `formatRate` (one decimal, a trailing `%`). */
    isPercent?: boolean;
    tooltipText?: string;
    trend?: KpiTrend;
    /** Lower-is-better metric: render the trend with inverted sentiment (a rise is bad). */
    invertTrend?: boolean;
    /** Drop the boxed chrome (border/background/padding) so the value sits flat
     * on its parent. Used by the Overview KPI catalog; legacy cards stay boxed. */
    flat?: boolean;
    /** Omitted leaves `toLocaleString`'s own default of 3. */
    maximumFractionDigits?: number;
    minimumFractionDigits?: number;
    /** Shortens a long figure (`$38,161.54` to `$38.2k`, `1,250,000` to
     * `1.3M`) and shows the exact one on hover. Short figures are left as
     * they are. */
    compact?: boolean;
    /** Shown in place of the value, with `shortValueTitle` (or the value
     * itself) on hover: a status too long for a tile, e.g. `Date N/A`. */
    shortValue?: string;
    shortValueTitle?: string;
}

/** Past this many characters, a `compact` tile shortens its figure. Keeps
 * `42,180` and `$0.00` whole while `123,456` and `$10,388.79` shorten. */
const COMPACT_AFTER_CHARS = 6;

const COMPACT_FORMAT = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
});

/** `38.2k`, `1.3M`, `2.1B`: Intl's compact notation with a lowercase k. */
export const formatCompact = (value: number, isCurrency?: boolean): string => {
    const short = COMPACT_FORMAT.format(Math.abs(value)).replace('K', 'k');
    return `${value < 0 ? '-' : ''}${isCurrency ? '$' : ''}${short}`;
};

export interface KpiTileProps {
    kpi: KpiTileData;
}

const DifferenceDisplay: React.FC<{ value: number; previousValue: number }> = ({
    value,
    previousValue,
}) => {
    const percent =
        previousValue === 0
            ? value === 0
                ? 0
                : 100
            : ((value - previousValue) / previousValue) * 100;

    return (
        <div className={style.difference}>
            <TrendArrow value={percent} size={16} />
        </div>
    );
};

/** Formats a KpiTrend delta as a signed string in the metric's own unit:
 * `currency` reads `-$5.36`, `duration` reads `+1m 30s` (seconds, matching the
 * value it sits under), `count` reads `+420` / `-0.33`, and `percent`
 * (percentage-points for a rate) reads `+5.36%`. */
export const formatTrendDelta = (trend: KpiTrend): string => {
    if (trend.unit === 'currency') {
        const abs = Math.abs(trend.delta).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        return `${trend.delta >= 0 ? '+' : '-'}$${abs}`;
    }
    if (trend.unit === 'duration') {
        // formatDurationSeconds clamps to a non-negative value, so carry the
        // sign here to keep a shrinking duration readable (`-1m 30s`).
        return `${trend.delta >= 0 ? '+' : '-'}${formatDurationSeconds(
            Math.abs(trend.delta)
        )}`;
    }
    const sign = trend.delta >= 0 ? '+' : '';
    if (trend.unit === 'count') {
        return `${sign}${trend.delta.toLocaleString('en-US', {
            maximumFractionDigits: 2,
        })}`;
    }
    // percent (percentage-points for a rate): 1 decimal for round, else 2.
    const abs = Math.abs(trend.delta);
    const decimals = Number.isInteger(abs) ? 1 : 2;
    return `${sign}${trend.delta.toFixed(decimals)}%`;
};

/** Inline trend row: arrow + delta + label. `invert` marks a lower-is-better
 * metric (e.g. churn), flipping the arrow color while keeping its direction. */
export const TrendRow: React.FC<{ trend: KpiTrend; invert?: boolean }> = ({
    trend,
    invert = false,
}) => (
    <div className={style.trend_row}>
        <TrendArrow
            value={
                trend.direction === 'up'
                    ? Math.abs(trend.delta)
                    : trend.direction === 'down'
                    ? -Math.abs(trend.delta)
                    : 0
            }
            size={14}
            showPercentage={false}
            // Lower-is-better can arrive two ways: the explicit `invert` prop
            // (card-level, e.g. churn via `invertTrend`) or `trend.invert` set on
            // the data by a source's `lowerIsBetter` (e.g. error rate). Honor both.
            positiveIsGood={!(invert || trend.invert)}
        />
        <span className={`${style.trend_delta} footnote_description`}>
            {formatTrendDelta(trend)} {trend.comparisonLabel}
        </span>
    </div>
);

export const KpiTile: React.FC<KpiTileProps> = ({ kpi }) => {
    const options =
        kpi.maximumFractionDigits === undefined &&
        kpi.minimumFractionDigits === undefined
            ? undefined
            : {
                  ...(kpi.minimumFractionDigits !== undefined && {
                      minimumFractionDigits: kpi.minimumFractionDigits,
                  }),
                  ...(kpi.maximumFractionDigits !== undefined && {
                      maximumFractionDigits: kpi.maximumFractionDigits,
                  }),
              };
    // Always called with the same arity: a non-numeric `kpi.value` (string)
    // feeds NaN through, which the hook treats as non-finite and returns as
    // is, so it never affects the string branch below.
    const tweenedValue = useTweenedValue(
        typeof kpi.value === 'number' ? kpi.value : NaN
    );

    const displayValue =
        typeof kpi.value === 'number'
            ? kpi.isPercent
                ? formatRate(tweenedValue)
                : kpi.isCurrency
                ? `$${formatTweenedNumber(tweenedValue, kpi.value, options)}`
                : formatTweenedNumber(tweenedValue, kpi.value, options)
            : kpi.value;

    const shortValue =
        kpi.shortValue ??
        (kpi.compact &&
        typeof kpi.value === 'number' &&
        Math.abs(kpi.value) >= 1000 &&
        displayValue.length > COMPACT_AFTER_CHARS
            ? formatCompact(kpi.value, kpi.isCurrency)
            : undefined);

    const content = (
        <div className={`${style.kpi_tile} ${kpi.flat ? style.flat : ''}`}>
            {kpi.title && (
                <div className={`${style.kpi_title} footnote_description`}>
                    {kpi.title}
                </div>
            )}
            {shortValue === undefined ? (
                <div className={`${style.kpi_value} h3_medium`}>
                    {displayValue}
                </div>
            ) : (
                // The tooltip is for the pointer; screen readers get the full
                // value as hidden text, since a div's aria-label is not
                // reliably announced.
                <Tooltip title={kpi.shortValueTitle ?? displayValue}>
                    <div className={`${style.kpi_value} h3_medium`}>
                        <span aria-hidden="true">{shortValue}</span>
                        <span className={style.visually_hidden}>
                            {displayValue}
                        </span>
                    </div>
                </Tooltip>
            )}
            {kpi.trend !== undefined ? (
                <TrendRow trend={kpi.trend} invert={kpi.invertTrend} />
            ) : kpi.previousValue !== undefined ? (
                <DifferenceDisplay
                    value={Number(kpi.value)}
                    previousValue={kpi.previousValue}
                />
            ) : null}
        </div>
    );

    const tip = kpi.description || kpi.tooltipText;
    return tip ? <Tooltip title={tip}>{content}</Tooltip> : content;
};

export default KpiTile;
